// ============================================================
// Alpine: shopProduct — halaman detail produk
// ============================================================

import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import { requireAuth, shopApi } from '../../helpers/shopAuth.js';

export function registerShopProduct() {
  Alpine.data('shopProduct', (product, imageSlug) => ({
    product,
    imageSlug,
    activeMedia: 0,
    qty: product?.min_order || 1,
    selected: {},
    customWidth: '',
    customHeight: '',
    notes: '',
    loading: false,
    pricePreview: null,
    error: '',

    init() {
      if (!this.product) return;

      this.product.variants?.forEach((group) => {
        if (group.options?.length) {
          this.selected[group.type] = group.options[0].id;
        }
      });

      this.qty = this.product.min_order || 1;
      this.simulatePrice();
      Alpine.store('wishlist')?.syncIds();
    },

    get selectedVariantIds() {
      return Object.values(this.selected).filter(Boolean);
    },

    formatRp(n) {
      return window.formatRupiah ? window.formatRupiah(n) : `Rp ${Number(n).toLocaleString('id-ID')}`;
    },

    async simulatePrice() {
      if (!this.product) return;

      try {
        const { data } = await axios.post(shopApi('/cart/price-simulate'), {
          product_id: this.product.id,
          qty: this.qty,
          variants: this.selectedVariantIds,
          custom_width_cm: this.customWidth || null,
          custom_height_cm: this.customHeight || null,
        });
        this.pricePreview = data.data?.pricing;
      } catch {
        this.pricePreview = null;
      }
    },

    selectVariant(type, id) {
      this.selected[type] = id;
      this.simulatePrice();
    },

    changeQty(delta) {
      const min = this.product?.min_order || 1;
      const max = this.maxQty;
      this.qty = Math.max(min, Math.min(max, this.qty + delta));
      this.simulatePrice();
    },

    async addToCart() {
      if (!this.product) return;

      if (!requireAuth(`/shop/products/${this.imageSlug}`)) return;

      if (this.product.tracks_stock && !this.product.in_stock) {
        this.error = 'Stok produk habis.';
        return;
      }

      if (this.product.tracks_stock && this.qty > this.maxQty) {
        this.error = `Stok tersedia hanya ${this.maxQty} ${this.product.price_unit}.`;
        return;
      }

      if (this.product.is_custom_size && (!this.customWidth || !this.customHeight)) {
        this.error = 'Ukuran kustom wajib diisi.';
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        const payload = {
          product_id: this.product.id,
          qty: this.qty,
          variants: this.selectedVariantIds,
          notes: this.notes || null,
        };

        if (this.product.is_custom_size) {
          payload.custom_width_cm = parseFloat(this.customWidth);
          payload.custom_height_cm = parseFloat(this.customHeight);
        }

        const { data } = await axios.post(shopApi('/cart'), payload);
        const count = data.data?.item_count ?? data.data?.summary?.item_count ?? 0;
        Alpine.store('cart').setCount(count);
        window.toast?.('Produk ditambahkan ke keranjang', 'success');
        window.location.href = '/shop/cart';
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal menambahkan ke keranjang.';
      } finally {
        this.loading = false;
      }
    },

    get maxQty() {
      if (!this.product?.tracks_stock) return 10000;
      return Math.max(this.product?.min_order || 1, Number(this.product?.stock) || 0);
    },

    async toggleWishlist() {
      if (!this.product?.id) return;
      if (!requireAuth(`/shop/products/${this.imageSlug}`)) return;
      await Alpine.store('wishlist').toggle(this.product.id);
    },
  }));

  Alpine.data('shopProductReviews', (productId) => ({
    productId,
    engagement: {
      likes_count: 0,
      reviews_count: 0,
      avg_rating: 0,
      has_liked: false,
      has_reviewed: false,
      user_review: null,
    },
    reviews: [],
    meta: { last_page: 1 },
    page: 1,
    form: { rating: 5, comment: '' },
    likeLoading: false,
    reviewLoading: false,
    reviewsLoading: false,
    error: '',
    success: '',

    async init() {
      await Promise.all([this.loadEngagement(), this.loadReviews(1)]);
    },

    async loadEngagement() {
      try {
        const { data } = await axios.get(shopApi(`/products/${this.productId}/engagement`));
        this.engagement = { ...this.engagement, ...(data.data || {}) };
      } catch { /* ignore */ }
    },

    async loadReviews(page = 1, append = false) {
      this.reviewsLoading = true;
      try {
        const { data } = await axios.get(shopApi(`/products/${this.productId}/reviews`), {
          params: { page, per_page: 8 },
        });
        const items = data.data || [];
        this.reviews = append ? [...this.reviews, ...items] : items;
        this.meta = data.meta || { last_page: 1 };
        this.page = page;
        if (!Alpine.store('auth')?.isLoggedIn) {
          this.engagement.reviews_count = data.meta?.total ?? items.length;
        }
      } catch {
        if (!append) this.reviews = [];
      } finally {
        this.reviewsLoading = false;
      }
    },

    async submitLike() {
      if (!requireAuth(window.location.pathname)) return;
      this.likeLoading = true;
      this.error = '';
      this.success = '';
      try {
        const { data } = await axios.post(shopApi(`/products/${this.productId}/like`));
        this.engagement = { ...this.engagement, ...(data.data?.engagement || {}) };
        const rewards = data.data?.rewards || {};
        if (rewards.xp || rewards.points) {
          this.success = `Terima kasih! +${rewards.xp || 0} XP${rewards.points ? `, +${rewards.points} poin` : ''}`;
        } else {
          this.success = data.message || 'Produk disukai.';
        }
      } catch (e) {
        this.error = e.response?.data?.message || e.response?.data?.errors?.like?.[0] || 'Gagal menyukai produk.';
      } finally {
        this.likeLoading = false;
      }
    },

    async submitReview() {
      if (!requireAuth(window.location.pathname)) return;
      if (!this.form.comment.trim()) {
        this.error = 'Komentar wajib diisi.';
        return;
      }
      this.reviewLoading = true;
      this.error = '';
      this.success = '';
      try {
        const { data } = await axios.post(shopApi(`/products/${this.productId}/review`), {
          rating: this.form.rating,
          comment: this.form.comment.trim(),
        });
        this.engagement = { ...this.engagement, ...(data.data?.engagement || {}) };
        const rewards = data.data?.rewards || {};
        if (rewards.xp || rewards.points) {
          this.success = `Ulasan tersimpan! +${rewards.xp || 0} XP${rewards.points ? `, +${rewards.points} poin` : ''}`;
        } else if (this.form.rating < 5) {
          this.success = 'Ulasan tersimpan. Bonus poin/XP hanya untuk rating bintang 5.';
        } else {
          this.success = data.message || 'Ulasan tersimpan.';
        }
        await this.loadReviews(1);
      } catch (e) {
        this.error = e.response?.data?.message
          || e.response?.data?.errors?.review?.[0]
          || e.response?.data?.errors?.comment?.[0]
          || 'Gagal mengirim ulasan.';
      } finally {
        this.reviewLoading = false;
      }
    },
  }));
}
